import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/app/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/app/components/ui/command'
import { Input } from '@/app/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover'
import {
  type CatalogOption,
  type CatalogOptionType,
  type CatalogPage,
  onlineCatalogService,
} from '@/app/services/online-catalog.service'
import { useDebounce } from '@/hooks/useDebounce'

type OptionKind = CatalogOptionType | 'products'

interface OnlineCatalogOptionSelectProps {
  label: string
  kind: OptionKind
  selected: CatalogOption | null
  onSelect: (option: CatalogOption) => void
  onClear?: () => void
  disabled?: boolean
}

export function OnlineCatalogOptionSelect({
  label,
  kind,
  selected,
  onSelect,
  onClear,
  disabled = false,
}: OnlineCatalogOptionSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [result, setResult] = useState<CatalogPage<CatalogOption> | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const requestVersion = useRef(0)
  const loadingMoreRef = useRef(false)
  const invalidateRequests = useCallback(() => { requestVersion.current++ }, [])

  const fetchPage = useCallback((page: number): Promise<CatalogPage<CatalogOption>> => kind === 'products'
    ? onlineCatalogService.products({ search: debouncedSearch || undefined, page, per_page: 20 })
      .then((response) => ({
        pagination: response.pagination,
        items: response.items.map((product) => ({ id: product.id, name: product.name, code: product.sku || undefined })),
      }))
    : onlineCatalogService.options(kind, { search: debouncedSearch || undefined, page, per_page: 20 }),
  [debouncedSearch, kind])

  useEffect(() => {
    if (!open) return
    const version = ++requestVersion.current
    let active = true
    setResult(null)
    setLoading(true)
    setLoadingMore(false)
    loadingMoreRef.current = false
    setError(false)

    void fetchPage(1).then((value) => { if (active && requestVersion.current === version) setResult(value) })
      .catch(() => { if (active) setError(true) })
      .finally(() => { if (active) setLoading(false) })

    return () => { active = false; invalidateRequests() }
  }, [fetchPage, invalidateRequests, open, retry])

  const hasMore = !!result && result.pagination.current_page < result.pagination.last_page
  const loadMore = useCallback(() => {
    if (!open || loading || loadingMoreRef.current || error || !result || !hasMore) return
    const version = requestVersion.current
    loadingMoreRef.current = true
    setLoadingMore(true)

    void fetchPage(result.pagination.current_page + 1).then((next) => {
      if (requestVersion.current !== version) return
      setResult((previous) => {
        if (!previous) return next
        const seen = new Set(previous.items.map((item) => item.id))
        return {
          pagination: next.pagination,
          items: [...previous.items, ...next.items.filter((item) => !seen.has(item.id))],
        }
      })
    }).catch(() => {
      if (requestVersion.current === version) setError(true)
    }).finally(() => {
      if (requestVersion.current === version) {
        loadingMoreRef.current = false
        setLoadingMore(false)
      }
    })
  }, [error, fetchPage, hasMore, loading, open, result])

  useEffect(() => {
    if (!open || !hasMore || !loadMoreRef.current || error) return
    const list = loadMoreRef.current.closest('[cmdk-list]')
    if (!list) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore()
    }, { root: list, threshold: 0.1 })
    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [error, hasMore, loadMore, open])

  const changeOpen = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearch('')
    }
  }

  return <div className="min-w-0 space-y-1">
    <span className="block text-sm font-medium text-card-foreground">{label}</span>
    <div className="flex min-w-0 gap-1">
      <Popover open={open && !disabled} onOpenChange={changeOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-label={`Seleccionar ${label.toLowerCase()}`}
            aria-expanded={open} disabled={disabled} className="min-w-0 flex-1 justify-between font-normal text-foreground">
            <span className="truncate">{selected?.name || `Seleccionar ${label.toLowerCase()}`}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0">
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <Input aria-label={`Buscar ${label}`} value={search} placeholder={`Buscar ${label.toLowerCase()}...`}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 border-0 px-0 shadow-none focus-visible:ring-0" />
            </div>
            <CommandList>
              {loading && <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando...
              </div>}
              {error && <div className="p-3 text-center text-sm text-destructive">
                No se pudieron cargar las opciones. <button type="button" className="underline" onClick={() => setRetry((value) => value + 1)}>Reintentar</button>
              </div>}
              {!loading && !error && result?.items.length === 0 && <CommandEmpty>Sin resultados.</CommandEmpty>}
              {!loading && (
              <CommandGroup>
                {result?.items.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={String(option.id)}
                    onSelect={() => {
                      onSelect(option)
                      changeOpen(false)
                    }}
                    className="text-card-foreground"
                  >
                    <Check
                      className={
                        selected?.id === option.id ? 'opacity-100' : 'opacity-0'
                      }
                    />

                    <span className="min-w-0 flex-1 truncate text-card-foreground">
                      {option.name}
                    </span>

                    {option.code && (
                      <span className="max-w-24 truncate text-xs text-muted-foreground">
                        {option.code}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
              {loadingMore && <div className="flex items-center justify-center py-2 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando más...
              </div>}
              {hasMore && <div ref={loadMoreRef} aria-hidden="true" className="h-px" />}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected && onClear && <Button type="button" variant="outline" size="icon" disabled={disabled}
        onClick={onClear} aria-label={`Limpiar ${label}`}><X className="h-4 w-4" /></Button>}
    </div>
  </div>
}
